// The warranty journey: the stages the service reports and the activity log's words. Presentation
// only: every state, date and summary comes from the service.

export const STAGE_ORDER = [
  "sold",
  "registered",
  "invoice",
  "verification",
  "active",
  "plan",
  "services",
  "referral",
];

export const STAGE_LABEL = {
  sold: "Sold",
  registered: "Registered",
  invoice: "Invoice",
  verification: "Verification",
  active: "Active",
  plan: "Plan",
  services: "Services",
  referral: "Referral",
};

export const STAGE_STATES = ["done", "current", "pending", "failed", "skipped"];

export const STATE_LABEL = {
  done: "Done",
  current: "In progress",
  pending: "Not yet",
  failed: "Failed",
  skipped: "Skipped",
};

// A stage as the screen draws it. An unknown state reads as pending, so a new service value never
// paints a stage green.
export const normaliseStage = (stage) => {
  const key = typeof stage?.key === "string" ? stage.key : "";
  const state = STAGE_STATES.includes(stage?.state) ? stage.state : "pending";
  return {
    key,
    label: (typeof stage?.label === "string" && stage.label) || STAGE_LABEL[key] || key || "-",
    state,
    at: typeof stage?.at === "string" ? stage.at : null,
    summary: typeof stage?.summary === "string" ? stage.summary : "",
    details: stage?.details && typeof stage.details === "object" ? stage.details : null,
  };
};

// The service's stages in its own order; a stage without a key is dropped.
export const journeyStages = (stages) =>
  (Array.isArray(stages) ? stages : []).map(normaliseStage).filter((stage) => stage.key !== "");

// The stage to open first: the first failed one, else the current one, else none.
export const initialOpenStage = (stages) =>
  (stages.find((stage) => stage.state === "failed") || stages.find((stage) => stage.state === "current"))
    ?.key || null;

// A stage's `details` as label and value rows. Values that are not text, numbers or true/false
// are left out (they would need their own screen); keys read as words.
const words = (key) =>
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^./, (first) => first.toUpperCase());

export const detailRows = (details) => {
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return [];
  }
  return Object.entries(details)
    .filter(([, value]) => ["string", "number", "boolean"].includes(typeof value) || value === null)
    .map(([key, value]) => ({
      key,
      label: words(key),
      value: value === null || value === "" ? "-" : typeof value === "boolean" ? (value ? "Yes" : "No") : String(value),
    }));
};

// The actor chip of an activity row.
export const ACTOR_LABEL = { rider: "Rider", dealer: "Dealer", staff: "Staff", system: "System", oms: "OMS" };
export const ACTOR_TONE = { rider: "info", dealer: "warning", staff: "success", system: "neutral", oms: "neutral" };

export const actorLabel = (actor) => ACTOR_LABEL[actor?.type] || (actor?.type ? String(actor.type) : "System");
export const actorTone = (actor) => ACTOR_TONE[actor?.type] || "neutral";
